import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogConfig } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { CustomPopupComponent } from '../components/custom-popup/custom-popup.component';
import { PopupConfig, PopupSize } from '../../core/models/popup-config.model';

@Injectable({
  providedIn: 'root'
})
export class PopupService {
  private dialog = inject(MatDialog);
  private breakpointObserver = inject(BreakpointObserver);

  /**
   * ✅ Open custom popup with configuration
   */
  open<T = any>(config: PopupConfig): MatDialogRef<CustomPopupComponent, T> {
    const dialogConfig = this.getDialogConfig(config);

    return this.dialog.open(CustomPopupComponent, {
      ...dialogConfig,
      data: { config }
    });
  }

  
  /**
   * ✅ Get Material Dialog configuration based on popup size
   */
  private getDialogConfig(config: PopupConfig): MatDialogConfig {
    const isMobile = this.breakpointObserver.isMatched([Breakpoints.XSmall, Breakpoints.Small]);
    
    // ✅ Set default mobile settings if not specified
    const mobilePosition = config.mobilePosition || 'bottom';
    const mobileHeight = config.mobileHeight || 'half';

    const baseConfig: MatDialogConfig = {
      disableClose: config.closeOnBackdropClick !== true,
      hasBackdrop: true,
      backdropClass: ['cdk-overlay-dark-backdrop', 'custom-popup-backdrop'],
      panelClass: this.getPanelClasses(config, isMobile),
      autoFocus: true,
      restoreFocus: true,
      closeOnNavigation: true,
      // ✅ Add position for mobile bottom sheet
      // ...(isMobile && mobilePosition === 'bottom' && {
      //   position: { bottom: '0' }
      // })
    };

    // ✅ Set dimensions based on size
    const dimensions = this.getDimensions(config.size || 'medium', isMobile);

    return {
      ...baseConfig,
      ...dimensions
    };
  }

  /**
   * ✅ Get popup dimensions based on size and device
   */
  private getDimensions(size: PopupSize, isMobile: boolean): Partial<MatDialogConfig> {
    // ✅ Mobile dimensions (full width)
    if (isMobile) {
      return {
        width: '100vw',
        maxWidth: '100vw',
        // minHeight: 'auto', // Half screen default
        maxHeight: '90vh'
      };
    }

    // ✅ Desktop dimensions
    const dimensionsMap: Record<PopupSize, Partial<MatDialogConfig>> = {
      small: {
        width: '400px',
        maxWidth: '90vw',
        // minHeight: '200px'
      },
      medium: {
        width: '560px',
        maxWidth: '90vw',
        // minHeight: '250px'
      },
      large: {
        width: '720px',
        maxWidth: '90vw',
        // minHeight: '300px'
      },
      xlarge: {
        width: '960px',
        maxWidth: '95vw',
        // minHeight: '400px'
      },
      fullscreen: {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh'
      }
    };

    return dimensionsMap[size];
  }

  /**
   * ✅ Get CSS panel classes
   */
  private getPanelClasses(config: PopupConfig, isMobile: boolean): string[] {
    const classes = ['custom-popup-panel'];

    if (config.size) {
      classes.push(`popup-size-${config.size}`);
    }

    // ✅ Add mobile-specific classes
    if (isMobile) {
      const mobilePosition = config.mobilePosition || 'bottom';
      const mobileHeight = config.mobileHeight || 'half';
      
      classes.push(`popup-mobile-${mobilePosition}`);
      classes.push(`popup-mobile-height-${mobileHeight}`);
    }

    if (config.customClass) {
      classes.push(config.customClass);
    }

    return classes;
  }

  /**
   * ✅ Close all open popups
   */
  closeAll(): void {
    this.dialog.closeAll();
  }
}